import { useState } from "react";
import { useParams } from "react-router-dom";
import { Loader, Toggle } from "rsuite";

import AlertBanner from "../../components/AlertBanner";
import PrescriberLayout from "../../components/PrescriberLayout";
import API from "../../utils/api";
import { useConfig } from "../../context/config-context";

/**
 * DisableNotificationsPage - Page publique pour désactiver les notifications quotidiennes
 *
 * Route: /prescriber-access/:permanentToken/disable-notifications
 *
 * Fonctionnalités:
 * - Affichage du toggle désactivé (lecture seule)
 * - Checkbox de confirmation obligatoire
 * - Appel API pour désactiver les notifications
 * - Banner de succès avec lien de réactivation
 * - Explications claires des conséquences
 */
export default function DisableNotificationsPage() {
  const { permanentToken } = useParams();
  const siteConfig = useConfig();

  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [success, setSuccess] = useState(false);

  /**
   * Désactive les notifications pour le prescripteur
   */
  const handleDisableNotifications = async () => {
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);

      const response = await API.post(
        `/prescriber-access/${permanentToken}/disable-notifications`,
        { notificationsEnabled: false, confirmationChecked: true }
      );

      if (response.data.success) {
        setSuccess(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      // Les erreurs sont gérées par l'interceptor axios (toast)
      console.error("Erreur désactivation notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Construire l'URL du portail prescripteur selon l'écosystème
  const prescriberPortalUrl = siteConfig.url || "https://prescripteurs.isisdiabete.fr";

  const footer = !success ? (
    <div className="flex justify-end">
      <button
        onClick={handleDisableNotifications}
        disabled={!confirmed || loading}
        className="px-3 py-1.5 bg-primary text-white rounded hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold"
      >
        {loading ? <Loader /> : "Enregistrer les préférences"}
      </button>
    </div>
  ) : (
    <p className="text-sm text-neutral-dark text-center">
      Vous pouvez maintenant fermer cette page.
    </p>
  );

  const primaryColor = siteConfig.cssVariables?.["--primary"] || "#24335d";

  return (
    <PrescriberLayout footer={footer} fullWidth>
      {/* Banner de succès */}
      {success && (
        <AlertBanner
          type="success"
          message={
            <>
              Vos préférences ont été enregistrées avec succès. Vous ne recevrez
              plus de notifications par email concernant les nouveaux documents
              de votre file active.
            </>
          }
          className="mb-6"
        />
      )}

      {/* Titre principal */}
      <h1
        className="text-2xl font-semibold mb-4"
        style={{ color: primaryColor }}
      >
        Préférences de notifications
      </h1>

      {/* Texte explicatif */}
      <div className="space-y-4 text-sm text-neutral-dark mb-6">
        <p>
          Vous ne souhaitez plus recevoir de notifications par mail concernant
          les nouveaux documents disponibles ? Vous pouvez désactiver cette
          option en cliquant sur le bouton ci-dessous. Attention : une fois la
          notification désactivée, vous ne serez plus informé(e) des nouveaux
          documents liés à votre file active et aucune autre communication ne
          sera envoyée.
        </p>
        <p>
          Pour réactiver l'accès à ces documents, vous pourrez créer votre accès
          sur{" "}
          <a
            href={prescriberPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary transition-colors text-neutral-dark"
            ref={(el) =>
              el &&
              el.style.setProperty("text-decoration", "underline", "important")
            }
          >
            {prescriberPortalUrl.replace("https://", "")}
          </a>
          .
        </p>
      </div>

      {/* Section Nouveaux documents */}
      <div className="space-y-0">
        <h2 className="text-base font-semibold text-neutral-dark">Nouveaux documents</h2>

        {/* Toggle */}
        <div className="flex items-center" style={{ marginTop: "10px" }}>
          <label className="text-sm text-neutral-dark flex items-center gap-2">
            Recevoir un mail pour la mise à disposition de nouveaux documents de
            votre file active :{" "}
            <span className="font-semibold mr-2">Désactivé</span>
            <Toggle checked={false} disabled={true} />
          </label>
        </div>

        {/* Checkbox de confirmation — sans border-t */}
        {!success && (
          <div className="pt-4">
            <label className="flex items-start gap-3 cursor-pointer hover:opacity-80 transition-opacity">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 w-4 h-4 text-primary bordertext-neutral-dark rounded focus:ring-primary focus:ring-2"
              />
              <span className="text-sm font-semibold text-neutral-dark select-none">
                J'ai bien pris connaissance qu'en cas de désactivation, je ne
                pourrai plus visualiser les nouveaux documents disponibles de ma
                file active.
              </span>
            </label>
          </div>
        )}
      </div>
    </PrescriberLayout>
  );
}
